import React, { useEffect, useState } from 'react';
import { Box, AppBar, Toolbar, Typography, Button, Container, Grid, Paper, Card, CardContent, CardActions, Avatar, Link as MuiLink, useTheme, useScrollTrigger, Slide, Divider, CardHeader, IconButton, Drawer, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import { typography } from 'theme/typography';
import ImageInsightsFull from './images/fsmart/insight-full.png';
import ImgReporting from './images/fsmart/insight-half.png';
import ImgCreateOrder from './images/fsmart/fs-createorder.png';
import ImgDriverAssign from './images/fsmart/fs-assigndriver.png';
import ImgRealtimeTrack from './images/fsmart/fs-trackorder.png';
import ImgInvoice from './images/fsmart/fs-invoice.png';
import ImgSmartTMS from './images/fsmart/fs-smarttms.png';
import ImgCompliance from './images/fsmart/fs-compliance.png';
import ImgFSAgent from './images/fsmart/fs-agent.svg';
import ImgAppStore from './images/fsmart/app_store.svg';
import ImgGooglePlay from './images/fsmart/google_play.png';
import FSLogo from './images/fsmart/FSlogo.svg';
import { tokens } from 'theme/tokens';
import { useRef } from 'react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import FTIcons from 'assets/icons/FTIcons';
import { useNavigate } from 'react-router';
import { MenuOutlined } from '@mui/icons-material';

const testimonials = [
  { name: 'Sygma Trucking', quote: 'SmartTMS cut our costs by 20% in 3 months!' },
  { name: 'GSR Trans Inc', quote: 'Tracking loads and payments are so easy now.' },
];
// Define a reusable section heading style
const sectionHeadingSx = {
  textAlign: 'center',
  fontWeight: 700,
  mb: 4,
  fontSize: { xs: '1.5rem', sm: '2.2rem' },
  color: (theme) => theme.palette.secondary.main,
  fontFamily: typography.fontFamily,
  letterSpacing: 0,
};

function ElevationScroll(props) {
  const { children } = props;
  const [elevate, setElevate] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      setElevate(window.scrollY > 50); // trigger after 50px
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return React.cloneElement(children, {
    elevation: elevate ? 4 : 0,
    style: {
      boxShadow: elevate ? '0 2px 16px rgba(0,0,0,0.08)' : 'none',
      transition: 'box-shadow 0.3s, background 0.3s',
      background: elevate ? '#fff' : 'transparent',
      backdropFilter: elevate ? 'none' : 'blur(0px)',
      borderBottom: elevate ? '1px solid #eee' : 'none',
    }
  });
}

const StyledAppBar = styled(AppBar)({
  boxShadow: 'none',
  transition: 'box-shadow 0.3s, background 0.3s',
  // background: 'transparent',
  zIndex: 1201, // Add zIndex to keep navbar above content (MUI's default for AppBar)
});

function Navbar(props) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isMenuDrawerOpen, setMenuDrawerOpen] = useState(false);

  return (
    <ElevationScroll {...props}>
      <StyledAppBar position='fixed' color='default'>
        <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 2, sm: 4 } }}>
          {/* Left: Logo */}
          <img src={FSLogo} alt='SmartTMS Logo' style={{ height: '70px', marginRight: 10 }} />
          {!isMobile && <Typography variant='h6' sx={{fontWeight: 600, color: colors.primary.main}}>
            FreightSmart
          </Typography>}
          {/* Center: Menu */}          
          <Box sx={{
            display: isMobile ? 'none' : 'flex',
            gap: { xs: 1, sm: 4 },
            flex: 1,
            justifyContent: 'center',
            alignItems: 'center',
            minWidth: 0
          }}>
            <MuiLink id='fs-home-navlink-platform' href='#platform' underline='none' color='inherit'>Platform</MuiLink>
            <MuiLink id='fs-home-navlink-pricing' href='#pricing' underline='none' color='inherit'>Pricing</MuiLink>
            <MuiLink id='fs-home-navlink-testimonial' href='#testimonials' underline='none' color='inherit'>Testimonials</MuiLink>
            <MuiLink id='fs-home-navlink-blog' href='/blog' underline='none' color='inherit'>Blog</MuiLink>
            {/* <MuiLink id='fs-home-navlink-contact' href='/contact' underline='none' color='inherit'>Contact Us</MuiLink> */}
            </Box>
          {/* Right: Oval Buttons */}
          <Box sx={{ display: 'flex', gap: 2, flex: '0 0 auto' }}>
            <Button
              id='fs-home-btn-trial'
              href='#pricing'
              variant='contained'
              sx={{
                borderRadius: 999,
                px: 2,
                py: 0.5,
                background: colors.primary.main,
                color: colors.base.white,
                fontWeight: 600,
                fontFamily: typography.fontFamily,
                boxShadow: 'none',
                fontSize: { xs: '0.85rem', sm: '0.92rem' },
                minWidth: 0,
                '&:hover': { color: colors.base.white, background: '#0059c9' }
              }}
            >
              Start Free Trial
            </Button>
            <Button
              id='fs-home-btn-login'
              variant='outlined'
              sx={{
                borderRadius: 999,
                px: 2,
                py: 0.5,
                borderColor: colors.primary.main,
                color: colors.primary.main,
                fontWeight: 600,
                fontFamily: typography.fontFamily,
                fontSize: { xs: '0.85rem', sm: '0.92rem' },
                minWidth: 0,
                background: colors.base.white,
                '&:hover': {
                  background: '#eaf1fa',
                  borderColor: '#0059c9',
                  color: '#0059c9'
                }
              }}
              onClick={() => navigate('/login')}
            >
              Login            
            </Button>
          </Box>
          {isMobile && <IconButton onClick={() => setMenuDrawerOpen((prev) => !prev)}>
            <MenuOutlined />
          </IconButton>}
        </Toolbar>
        <Drawer
          anchor='right'
          open={isMenuDrawerOpen} 
          onClose={() => setMenuDrawerOpen(false)}
          ModalProps={{
            keepMounted: false,
            style: { left: 'auto' }
          }}
          slotProps={{
            paper: {
              sx: {
                width: '50%',
                height: '100%',
                top: 72,
                p: 2,
                px: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
              }
            }
          }}          
        >
          <MuiLink id='fs-home-navlink-platform' href='#platform' underline='none' color='inherit'>Platform</MuiLink>
          <Divider />
          <MuiLink id='fs-home-navlink-pricing' href='#pricing' underline='none' color='inherit'>Pricing</MuiLink>
          <Divider />
          <MuiLink id='fs-home-navlink-testimonial' href='#testimonials' underline='none' color='inherit'>Testimonials</MuiLink>
          <Divider />
          <MuiLink id='fs-home-navlink-blog' href='/blog' underline='none' color='inherit'>Blog</MuiLink>
          <Divider />
        </Drawer>        
      </StyledAppBar>
    </ElevationScroll>
  );
}

function IntroSection() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const heroMessage = (
    <>
      Brokers & Carriers<br />
      <span style={{ color: colors.primary.main }}>Automate & Optimize</span><br />
      {/* <span style={{fontStyle: 'oblique', fontWeight: 400, fontSize: '32px', fontFamily: 'auto'}}>your</span><br/> */}
      your Logistics Operations.
    </>
  );

  return (
    <Box
      component='section'
      sx={{
        background: '#fff',
        py: { xs: 4, sm: 8 },
        mb: { xs: 2, sm: 4 }
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            justifyContent: 'center',
            gap: { xs: 4, md: 8 }
          }}
        >
          {/* Text Side */}
          <Box
            sx={{
              flex: 1,
              textAlign: { xs: 'center', md: 'left' },
              mb: { xs: 3, md: 0 }
            }}
          >
            <Typography
              variant='header'
              sx={{
                fontWeight: 600,
                mb: 2,
                transition: 'color 0.5s',
              }}
            >
              {heroMessage}
            </Typography>
          </Box>
          {/* Image Side */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              justifyContent: { xs: 'center', md: 'flex-end' },
              alignItems: 'center'
            }}
          >
            <Box
              component='img'
              src={ImgFSAgent}
              alt='FreightSmart Platform'
              sx={{
                width: '100%',
                maxWidth: 580,
                borderRadius: 3,
                display: 'block'
              }}
            />
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

function HeroSection() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const heroMessages = [
    `Unified Platform for Brokers and Carriers to <span>Streamline & Optimize</span> Logistics Operations`,
    'Optimize Freight, Delight Customers',
    'Smarter Routing. Real-Time Tracking. Automated Billing.'
  ];
  const [activeMsg, setActiveMsg] = React.useState(0);

  React.useEffect(() => {
    const onScroll = () => {
      const scrollY = window.scrollY;
      const idx = Math.min(Math.floor(scrollY / 300), heroMessages.length - 1);
      setActiveMsg(idx);
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const [windowWidth, setWindowWidth] = React.useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  React.useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const imageWidth = Math.max(220, Math.min(windowWidth * 0.75, 1200));

  return (
    <Box
      component='section'
      sx={{
        width: '100vw',
        minHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        background: 'linear-gradient(180deg, #fefefe 60%, #fff 100%)',
        pt: { xs: 2, sm: 6 },
        pb: { xs: 3, sm: 4 },
        gap: 4,
        fontFamily: typography.fontFamily,
        boxSizing: 'border-box',
        px: 0,
        position: 'relative',
        left: '50%',
        right: '50%',
        marginLeft: '-50vw',
        marginRight: '-50vw',
      }}
    >
      <Container maxWidth={false} sx={{ textAlign: 'center', px: { xs: 2, sm: 6, md: 12 }, maxWidth: '1600px' }}>
        <Typography
          variant='header'
          sx={{
            ...typography.header,
            mb: 3,
            fontFamily: typography.fontFamily,
            fontSize: { xs: '2rem', sm: typography.header.fontSize },
            fontWeight: 600,
            color: colors.neutral[600],
            transition: 'color 0.5s',
          }}
        >
          Brokers and Carriers <br/><span style={{color: colors.primary.main}}>Automate & Streamline</span><br/><span color={colors.neutral[400]}>your</span> Freight Operations

          {/* Unified Platform <span style={{color: colors.neutral[300]}}>for</span> <br/>Brokers and Carriers <br/><span style={{color: colors.neutral[300]}}>to</span> <span style={{color: colors.primary.main}}>Streamline & Optimize</span> <br/>Freight Operations */}
        </Typography>
        <Typography
          variant='body1'
          sx={{
            ...typography.body1,
            color: colors.base.black,
            maxWidth: 600,
            mx: 'auto',
            mt: 3,
            mb: 3,
            fontFamily: typography.fontFamily,            
          }}
        >
          Unified Platform for Brokers and Carriers to optimize their freight operations, reduce costs, and delight customers.
        </Typography>
        <Button
          id='fs-home-btn-trial-hero'
          href='#pricing'
          variant='contained'
          sx={{
            background: colors.primary.main,
            color: colors.base.white,
            px: 3,
            py: 1,
            mr:1,
            borderRadius: 2,
            fontWeight: 600,
            fontFamily: typography.fontFamily,
            fontSize: { xs: '0.85rem', sm: '0.92rem' },
            minWidth: 0,
            boxShadow: 'none',
            '&:hover': { color: colors.base.white, background: '#0059c9' }
          }}
        >
          Start Free Trial
        </Button>
        <Button
          id='fs-home-btn-demo-hero'    
          href='#pricing'
          variant='outlined'
          sx={{
            background: colors.primary.main,
            color: colors.base.white,
            px: 3,
            py: 1,
            ml: 1,
            borderRadius: 2,
            fontWeight: 600,
            fontFamily: typography.fontFamily,
            fontSize: { xs: '0.85rem', sm: '0.92rem' },
            minWidth: 0,
            boxShadow: 'none',
            '&:hover': { color: colors.base.white, background: '#0059c9' }
          }}         
        >
          View Demo
        </Button>
      </Container>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <Box
          sx={{
            width: imageWidth,
            maxWidth: '95vw',
            borderRadius: 4,
            overflow: 'hidden',
            boxShadow: '0 8px 48px 0 rgba(0,112,243,0.10), 0 1.5px 8px 0 rgba(0,0,0,0.04)',
            background: 'linear-gradient(120deg, #eaf1fa 60%, #fff 100%)',
            mx: 'auto',
            transition: 'width 0.3s',
          }}
        >
          <Box
            component='img'
            src={ImageInsightsFull}
            alt='Platform Screenshot'
            sx={{
              width: '100%',
              height: 'auto',
              display: 'block',
              objectFit: 'cover',
              borderRadius: 4,
              boxShadow: '0 2px 16px #eaf1fa',
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}

const workflowSteps = [
  {
    label: 'Create Order',
    heading: 'Create Your Order',
    subheading: 'Start your shipment with a few clicks.',
    desc: [
      'Enter shipment and customer details.',
      'Attach documents and set pickup/delivery.',
      'Save as draft or submit for dispatch.'
    ],
    image: ImgCreateOrder // Replace with your own image per step if available
  },
  {
    label: 'Dispatch Order',
    heading: 'Assign Driver',
    subheading: 'Assign Driver and notify instantly.',
    desc: [
      'Select carrier or driver from your network.',
      'Optimize route and schedule.',
      'Send digital dispatch notifications.'
    ],
    image: ImgDriverAssign // Replace with your own image per step if available
  },
  {
    label: 'Track Order',
    heading: 'Real-time Tracking',
    subheading: 'Stay updated with orders.',
    desc: [
      'Live Status & Timing updates.',
      'Exception alerts for delays or issues.',
      'Share tracking with customers.'
    ],
    image: ImgRealtimeTrack
  },
  {
    label: 'Generate Invoice',
    heading: 'Automated Invoicing',
    subheading: 'Get paid faster, with less work.',
    desc: [
      'Auto-generate invoice after delivery.',
      'Send to customer with one click.',
      'Track payment status in your dashboard.'
    ],
    image: ImgInvoice
  },
];

function PlatformSection() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [current, setCurrent] = React.useState(0);

  // Auto-advance carousel every 4 seconds
  React.useEffect(() => {
    const interval = setInterval(() => setCurrent((c) => (c + 1) % workflowSteps.length), 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Box component='section' id='platform' sx={{ background: '#f9f9f9', py: { xs: 4, sm: 8 }, width: '100%' }}>
      <Container maxWidth='md'>
        <Typography variant="h2" sx={sectionHeadingSx}>
          Our Platform
        </Typography>
        {/* Tabs Step Group */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            mb: 4,
            background: '#f0f1f3',
            borderRadius: 999,
            px: { xs: 1, sm: 2 },
            py: 1,
            width: '100%',
            maxWidth: 600,
            mx: 'auto',
          }}
        >
          <Tabs
            value={current}
            onChange={(_, idx) => setCurrent(idx)}
            variant='fullWidth'
            TabIndicatorProps={{ style: { display: 'none' } }}
            sx={{
              minHeight: 0,
              width: '100%',
              '& .MuiTabs-flexContainer': {
                gap: { xs: 0.5, sm: 1 },
              },
            }}
          >
            {workflowSteps.map((step, idx) => (
              <Tab
                key={step.label}
                label={step.label}
                sx={{
                  borderRadius: 999,
                  minHeight: 32,
                  minWidth: 0,
                  px: { xs: 1, sm: 1.5 },
                  py: 0.2,
                  mx: 0.25,
                  fontWeight: current === idx ? 700 : 500,
                  fontFamily: typography.fontFamily,
                  fontSize: { xs: '0.85rem', sm: '0.92rem' },
                  background: current === idx ? '#fff' : 'transparent',
                  color: current === idx ? colors.primary.main : '#555',
                  boxShadow: 'none',
                  border: 'none',
                  transition: 'all 0.2s',
                  zIndex: current === idx ? 1 : 0,
                  whiteSpace: 'nowrap',
                  textTransform: 'none',
                  lineHeight: 1.2,
                  '&:hover': {
                    background: current === idx ? '#fff' : '#f5f7fa',
                    color: colors.primary.main,
                  }
                }}
              />
            ))}
          </Tabs>
        </Box>
        {/* Carousel Section */}
        <Grid
          container
          justifyContent='center'
          alignItems='center'
          spacing={0}
          sx={{
            flexDirection: { xs: 'column', md: 'row' },
            mb: 2,
            background: 'none',
            boxShadow: 'none',
            borderRadius: 0,
            p: 0,
            minHeight: 260
          }}
        >
          {/* Text Side */}
          <Grid
            size={{ xs: 12, sm: 4 }}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: { xs: 'center', md: 'flex-start' },
              textAlign: { xs: 'center', md: 'left' },
              px: { xs: 0, md: 2 },
              py: { xs: 2, md: 0 }
            }}
          >
            <Typography variant='h5' sx={{ mb: 1, fontSize: { xs: '1.2rem', sm: '1.7rem' }, color: colors.primary.main, fontWeight: 700 }}>
              {workflowSteps[current].heading}
            </Typography>
            <Typography sx={{ color: '#222', fontSize: { xs: '1rem', sm: '1.15rem' }, mb: 1, fontWeight: 500 }}>
              {workflowSteps[current].subheading}
            </Typography>
            <Box>
              {workflowSteps[current].desc.map((point, i) => (
                <Box display={'flex'} alignItems='center' gap={1} key={i}>
                  <FTIcons.CheckCircleIcon fill={colors.green[500]} />
                  <Typography variant={'body1'} sx={{ color: '#555' }}>{point}</Typography>
                </Box>
              ))}
            </Box>
          </Grid>
          {/* Image Side */}
          <Grid
            item
            size={{ xs: 12, sm: 8 }}
            sx={{
              display: 'flex',
              justifyContent: { xs: 'center', md: 'flex-end' },
              alignItems: 'center',
              px: { xs: 0, md: 2 },
              py: { xs: 2, md: 0 }
            }}
          >
            <Box
              component='img'
              src={workflowSteps[current].image}
              alt={workflowSteps[current].label}
              sx={{
                width: '100%',
                // maxWidth: 480,
                // height: { xs: 200, sm: 260, md: 320 },
                maxHeight: 340,
                objectFit: 'contain', // Ensures full image is visible, not trimmed
                borderRadius: 3,
                display: 'block',
                background: '#f5f7fa'
              }}
            />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

function PricingSection() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  const plans = [
    {
      name: 'Basic',
      tagline: 'Great for Small Teams',
      price: '$15', //75¢ per load
      features: ['20 Loads', 'Single User', 'Realtime Dispatch', 'Invoice Management', 'Driver Settlement', 'Fleet Management', 'Asset Management', 'Driver Mobile App',],
      recommended: false,
    },
    {
      name: 'Regular',
      tagline: 'Perfect for Growing Business',
      price: '$75', //65¢ per load
      addOn: '55¢ per additional load',
      features: ['115 Loads', 'Everything in Basic', '10 Users', 'Reporting', 'Compliance', 'Document Manager', 'Priority Support'],
      recommended: true,
    },
    {
      name: 'Premium',
      tagline: 'For Large Scale Operations',
      price: '$135', //55¢ per load
      addOn: '45¢ per additional load',
      features: ['245 Loads', 'All Regular Features', 'Advanced Reporting', 'Dedicated Manager'],
      recommended: false,
    },
  ];

  return (
    <Box
      component="section"
      id="pricing"
      sx={{
        background: '#f9f9f9', // Changed from #fff to a light grey
        py: { xs: 4, sm: 8 },
        width: '100%',
      }}
    >
      <Container maxWidth="md">
        <Typography variant="h2" sx={sectionHeadingSx}>
          Pricing
        </Typography>
        <Grid container spacing={2} justifyContent="center" alignItems="stretch">
          {plans.map((plan, idx) => {
            return (
              <Grid
                key={plan.name}
                size={{ xs: 12, sm: 6, md: 4 }}
                sx={{ display: 'flex', justifyContent: 'center', alignItems: 'stretch' }}
              >
                <Box
                  sx={{
                    width: '100%',
                    maxWidth: 390,
                    minHeight: 380,
                    borderRadius: 4,
                    background: plan.recommended ? colors.primary.main : 'transparent',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    boxShadow: `${plan.recommended} && 0 4px 24px 0 rgba(0,112,243,0.18)`,
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: colors.base.white, textAlign: 'center', mb: 1 }}>RECOMMENDED</Typography>
                  <Card
                    sx={{
                      minHeight: 460,
                      background: '#fff',
                      borderRadius: 4,
                      boxShadow: '0 2px 12px 0 rgba(0,0,0,0.06)',
                    }}
                  >
                    <CardHeader
                      sx={{
                        position: 'relative',
                        zIndex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        p: 4,
                      }}
                      title={<Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h3" sx={{ mb: 1, fontWeight: 700 }}>
                          {plan.name}
                        </Typography>
                        <Typography variant="body1" sx={{ color: colors.neutral[700] }}>
                          {plan.tagline}
                        </Typography>
                        <Box mt={2}>
                          <Typography variant='h2' sx={{ fontWeight: 700, mr: 0.5 }}>{plan.price}</Typography>
                          <Typography variant="body1" sx={{ color: colors.neutral[500] }}>
                            per month
                          </Typography>
                          {plan.addOn && <Typography variant="body1" sx={{ color: colors.neutral[700], textAlign: 'center' }}><FTIcons.StarIcon width={12} height={12} fill={colors.secondary.main} />{plan.addOn}</Typography>}
                        </Box>
                      </Box>
                      }
                    />
                    <Divider />
                    <CardContent sx={{minHeight: 275}}>
                      <Box component="ul" sx={{ p: 4, m: 1, listStyle: 'none', p: 0, mb: 2, width: '100%' }}>
                        {plan.features.map((f, i) => (
                          <Typography
                            variant="subtitle2"
                            component="li"
                            key={i}
                            sx={{
                              mb: 1,
                              color: colors.neutral[700],
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              fontWeight: i === 0 ? 600 : 400
                            }}
                          >
                            <Box component="span" sx={{ color: colors.primary.light, fontWeight: 700, mr: 1 }}>✓</Box>
                            {f}
                          </Typography>
                        ))}
                      </Box>
                    </CardContent>
                    <CardActions>
                      <Button
                        id={`fs-home-btn-trial-${plan.name.toLowerCase()}`}
                        variant="contained"
                        sx={{
                          m: 'auto',
                          borderRadius: 999,
                          px: 2,
                          py: 1,
                          background: colors.secondary.main,
                          color: '#fff  ',
                          fontWeight: 600,
                          fontSize: '1rem',
                          borderColor: '#fff',
                          boxShadow: '0 2px 8px #eaf1fa',
                          '&:hover': {
                            background: colors.secondary.dark,
                            color: '#fff',
                            borderColor: colors.secondary.soft
                          }
                        }}
                        onClick={() => navigate('/register?plan=' + plan.name.toLowerCase())}
                      >
                        Start Your Free Trial
                      </Button>
                    </CardActions>
                  </Card>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </Box>
  );
}


function TestimonialSection() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  return (
    <Box component='section' id='testimonials' sx={{ background: '#f9f9f9', py: { xs: 4, sm: 8 }, width: '100%' }}>
      <Container maxWidth='md'>
        <Typography variant="h2" sx={sectionHeadingSx}>
          What Our Customers Say
        </Typography>
        <Grid container justifyContent='center' spacing={4}>
          {testimonials.map((t, idx) => (
            <Grid item xs={12} sm={6} md={4} key={idx}>
              <Card sx={{ background: '#fff', borderRadius: 2, boxShadow: 2, p: 3, minWidth: 260, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <CardContent>
                  <Typography sx={{ fontStyle: 'italic', color: '#333', mb: 1.5 }}>'{t.quote}'</Typography>
                  <Typography sx={{ fontWeight: 600, color: colors.primary.main }}>{t.name}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}

function CustomerIntegrationStrip() {
  // You can replace these with logo images if available
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const partners = [
    { name: 'GSRTrans Inc', logo: null },
    { name: 'Newport Carriers', logo: null },
    { name: 'Sygma Trucking', logo: null },
    { name: 'Sucha Transport', logo: null },
    { name: 'M&I Logistics', logo: null },
  ];

  // Duplicate the list for seamless looping
  const scrollingPartners = [...partners, ...partners];

  const stripRef = useRef();
  const [animationDuration, setAnimationDuration] = React.useState(20); // seconds

  // Responsive duration based on viewport width
  React.useEffect(() => {
    function handleResize() {
      if (window.innerWidth < 600) setAnimationDuration(14);
      else if (window.innerWidth < 900) setAnimationDuration(18);
      else setAnimationDuration(20);
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <Paper
      elevation={3}
      sx={{
        p: 2,
        width: '100%',
        background: colors.secondary.main,
        overflow: 'hidden',
        borderBottom: '1px solid #eaf1fa',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        boxShadow: '0 4px 24px 0 rgba(0,0,0,0.08)', // <-- Add elevation
        zIndex: 10, // ensure it's above background
        position: 'relative'
      }}
    >
      <Typography variant='subtitle2' sx={{ textAlign: 'center', fontWeight: 700, color: colors.base.white, m: 1 }}>
        Our Trusted Partners
      </Typography>
      <Box
        sx={{
          width: '100%',
          maxWidth: 1100,
          overflow: 'hidden',
          position: 'relative',
          height: 48,
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <Box
          ref={stripRef}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 4, sm: 8 },
            whiteSpace: 'nowrap',
            animation: `scroll-left ${animationDuration}s linear infinite`
          }}
        >
          {scrollingPartners.map((partner, idx) => (
            <Box
              key={partner.name + idx}
              sx={{
                fontWeight: 400,
                fontSize: { xs: 20, sm: 26 },
                color: colors.base.white,
                letterSpacing: 1,
                display: 'flex',
                alignItems: 'center',
                minWidth: 100,
                justifyContent: 'center',
                px: 1,
                opacity: 0.92,
                transition: 'opacity 0.3s'
              }}
            >
              {/* If you have a logo, use <img src={partner.logo} ... /> */}
              {partner.logo ? (
                <img src={partner.logo} alt={partner.name} style={{ height: 36, marginRight: 12 }} />
              ) : (
                partner.name
              )}
            </Box>
          ))}
        </Box>
        {/* Keyframes for smooth scrolling */}
        <style>
          {`
            @keyframes scroll-left {
              0% { transform: translateX(0); }
              100% { transform: translateX(-50%); }
            }
          `}
        </style>
      </Box>
    </Paper>
  );
}

function Footer() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return (
    <Box
      component='footer'
      sx={{
        background: 'linear-gradient(180deg, #1A1A1A 60%, #000 100%)',
        borderTop: 'none',
        py: { xs: 6, sm: 8 },
        color: '#fff',
        mt: 4,
      }}
    >
      <Container maxWidth="md">
        <Grid container spacing={4} justifyContent="space-between">
          {/* About */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>            
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>FreightSmart</Typography>
            <Typography variant="body2" sx={{ color: '#bbb' }}>4695 Chabot Drive</Typography>
            <Typography variant="body2" sx={{ color: '#bbb' }}>Pleasanton, CA. 94588</Typography>
            <Box>
              <MuiLink href='https://apps.apple.com/us/app/freightsmart-tms/id6739815667'><img src={ImgAppStore} alt="Download on the App Store" style={{ height: 40, marginTop: 12, marginRight: 8 }} /></MuiLink>
              <MuiLink href='https://play.google.com/store/apps/details?id=com.freightsmart.tms'><img src={ImgGooglePlay} alt="Get it on Google Play" style={{ height: 40, marginTop: 12 }} /></MuiLink>
            </Box>
          </Grid>
          {/* Quick Links */}
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Quick Links
            </Typography>
            <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
              <li><MuiLink href="#platform" sx={{ color: '#bbb', textDecoration: 'none', '&:hover': { color: colors.primary.main } }}>Platform</MuiLink></li>
              <li><MuiLink href="#pricing" sx={{ color: '#bbb', textDecoration: 'none', '&:hover': { color: colors.primary.main } }}>Pricing</MuiLink></li>
              <li><MuiLink href="#testimonials" sx={{ color: '#bbb', textDecoration: 'none', '&:hover': { color: colors.primary.main } }}>Testimonials</MuiLink></li>
              <li><MuiLink href="/blog" sx={{ color: '#bbb', textDecoration: 'none', '&:hover': { color: colors.primary.main } }}>Blog</MuiLink></li>
            </Box>
          </Grid>
          {/* Resources */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Resources
            </Typography>
            <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
              <li><MuiLink href="/privacy" sx={{ color: '#bbb', textDecoration: 'none', '&:hover': { color: colors.primary.main } }}>Privacy Policy</MuiLink></li>
              <li><MuiLink href="/terms" sx={{ color: '#bbb', textDecoration: 'none', '&:hover': { color: colors.primary.main } }}>Terms of Service</MuiLink></li>
              <li><MuiLink href="/support" sx={{ color: '#bbb', textDecoration: 'none', '&:hover': { color: colors.primary.main } }}>Support</MuiLink></li>
            </Box>
          </Grid>
          {/* Contact Info */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Contact
            </Typography>
            <Typography variant="body2" sx={{ color: '#bbb', mb: 1 }}>
              Email: <MuiLink href="mailto:support@freightsmart.ai" sx={{ color: '#bbb', textDecoration: 'none', '&:hover': { color: colors.primary.main } }}>support@freightsmart.ai</MuiLink>
            </Typography>
            <Typography variant="body2" sx={{ color: '#bbb', mb: 1 }}>
              <FTIcons.WhatsAppIcon width={20} height={20} fill={colors.success.main} /> <MuiLink href="tel:+9254607781" sx={{ color: '#bbb', textDecoration: 'none', '&:hover': { color: colors.primary.main } }}>+1 (925) 460-7781</MuiLink>
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <MuiLink href="https://www.linkedin.com/company/freightsmart-ai/?viewAsMember=true" target="_blank" rel="noopener" aria-label="LinkedIn" sx={{ color: '#fff', '&:hover': { color: colors.primary.main } }}>
                <FTIcons.LinkedInIcon width={28} height={28} fill={colors.primary.main} />
              </MuiLink>
              <MuiLink href="https://www.youtube.com/@FreightSmart" target="_blank" rel="noopener" aria-label="YouTube" sx={{ color: '#fff', '&:hover': { color: colors.primary.main } }}>
                <FTIcons.YouTubeIcon width={28} height={28} fill={'#FF0033'} />
              </MuiLink>
              <MuiLink href="https://www.facebook.com/profile.php?id=61581040647099" target="_blank" rel="noopener" aria-label="Facebook" sx={{ color: '#fff', '&:hover': { color: colors.primary.main } }}>
                <FTIcons.MetaIcon width={28} height={28} fill={'#0180FA'}/>
              </MuiLink>
              <MuiLink href="https://www.instagram.com/freightsmart.us/" target="_blank" rel="noopener" aria-label="Instagram" sx={{ color: '#fff', '&:hover': { color: colors.primary.main } }}>
                <FTIcons.InstagramIcon width={28} height={28} fill={colors.yellow[600]}/>
              </MuiLink>
            </Box>
          </Grid>
        </Grid>
        <Divider sx={{ my: 4, borderColor: '#222' }} />
        <Typography sx={{ color: '#bbb', textAlign: 'center', fontSize: '0.95rem', mt: 2 }}>
          © {new Date().getFullYear()} FreightSmart. All rights reserved.
        </Typography>
      </Container>
    </Box>
  );
}

const products = [
  {
    name: 'Smart TMS',
    subtitle: 'Modern Transportation Management System',
    info: 'A unified platform for brokers and carriers to manage orders, dispatch, tracking, billing, and analytics. Streamline your logistics operations and boost efficiency with automation and real-time insights.',
    image: ImgSmartTMS // Replace with actual product image if available
  },
  {
    name: 'Compliance',
    subtitle: 'Automated Compliance & Safety',
    info: 'Stay audit-ready with automated document management, driver qualification tracking, ELD integrations, and proactive compliance alerts. Reduce risk and simplify regulatory requirements.',
    image: ImgCompliance // Replace with actual product image if available
  },
  {
    name: 'Reporting',
    subtitle: 'Advanced Analytics & Reporting',
    info: 'Unlock actionable insights with customizable dashboards, shipment analytics, cost breakdowns, and customer scorecards. Make data-driven decisions to grow your business.',
    image: ImgReporting // Replace with actual product image if available
  }
];

function ProductsSection() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // Convert info to bullet points for each product
  const productBullets = [
    [
      'Unified platform for brokers and carriers',
      'Manage orders, dispatch, tracking, billing, and analytics',
      'Boost efficiency with automation and real-time insights'
    ],
    [
      'Automated document management',
      'Driver qualification tracking & ELD integrations',
      'Proactive compliance alerts and audit readiness'
    ],
    [
      'Customizable dashboards and shipment analytics',
      'Cost breakdowns and customer scorecards',
      'Make data-driven decisions to grow your business'
    ]
  ];

  return (
    <Box sx={{ width: '100%', py: { xs: 4, sm: 8 }, mb: 2 }}>
      <Container maxWidth='lg'>
        <Typography variant="h2" sx={sectionHeadingSx}>
          Our Products
        </Typography>
        {products.map((product, idx) => (
          <Box
            key={product.name}
            sx={{
              display: 'flex',
              flexDirection: {
                xs: 'column',
                md: idx % 2 === 0 ? 'row' : 'row-reverse'
              },
              alignItems: 'center',
              justifyContent: 'center',
              gap: { xs: 4, md: 6 },
              mb: 8
            }}
          >
            {/* Text Side */}
            <Box
              sx={{
                flex: 1,
                textAlign: { xs: 'center', md: idx % 2 === 0 ? 'left' : 'right' },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: { xs: 'center', md: idx % 2 === 0 ? 'flex-start' : 'flex-end' },
                px: { xs: 1, md: 3 }
              }}
            >
              <Typography variant='h3' sx={{ color: colors.primary.main, fontWeight: 700, mb: 1 }}>
                {product.name}
              </Typography>
              <Typography variant='h6' sx={{ fontWeight: 500, mb: 2, color: colors.neutral[700] }}>
                {product.subtitle}
              </Typography>
              <Box
                component="ul"
                sx={{
                  pl: 2,
                  mb: 0,
                  textAlign: { xs: 'left', md: idx % 2 === 0 ? 'left' : 'right' },
                  // maxWidth: 480
                }}
              >
                {productBullets[idx].map((point, i) => (
                  <Typography
                    variant="subtitle1"
                    component="li"
                    key={i}
                    sx={{
                      color: '#555',                      
                      mb: 1.2,
                      listStyle: 'square',
                      ml: 2
                    }}
                  >
                   {point}
                  </Typography>
                ))}
              </Box>
            </Box>
            {/* Image Side */}
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                justifyContent: { xs: 'center', md: idx % 2 === 0 ? 'flex-end' : 'flex-start' },
                alignItems: 'center',
                px: { xs: 1, md: 3 }
              }}
            >
              <Box
                component='img'
                src={product.image}
                alt={product.name}
                sx={{
                  // width: { xs: '100%', sm: 520, md: 700 },
                  maxWidth: '100%',
                  // height: { xs: 320, sm: 420, md: 480 },
                  objectFit: 'contain',
                  borderRadius: 3,
                  boxShadow: '0 2px 16px #eaf1fa',
                  // background: 'linear-gradient(120deg, #eaf1fa 60%, #fff 100%)',
                  display: 'block'
                }}
              />
            </Box>
          </Box>
        ))}
      </Container>
    </Box>
  );
}

function BlogSection() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // Dummy blog data
  const blogs = [
    {
      title: 'How AI is Transforming Logistics',
      summary: 'Discover how artificial intelligence is revolutionizing freight management, route optimization, and customer experience in the logistics industry.',
      image: ImgSmartTMS,
      slug: 'ai-transforming-logistics'
    },
    {
      title: 'Compliance Best Practices for Carriers',
      summary: 'Stay ahead of regulations with these essential compliance tips for carriers and brokers. Learn how automation can help you stay audit-ready.',
      image: ImgCompliance,
      slug: 'compliance-best-practices'
    },
    {
      title: 'Unlocking Insights with FreightSmart Reporting',
      summary: 'See how advanced analytics and reporting can help you make smarter decisions, reduce costs, and grow your logistics business.',
      image: ImageInsightsFull,
      slug: 'freightsmart-reporting-insights'
    }
  ];

  return (
    <Box component="section" id="blog" sx={{ background: '#fff', py: { xs: 4, sm: 8 }, width: '100%' }}>
      <Container maxWidth="md">
        <Typography variant="h2" sx={sectionHeadingSx}>
          From Our Blog
        </Typography>
        <Grid container spacing={4} justifyContent="center">
          {blogs.map((blog, idx) => (
            <Grid key={blog.slug} size={{ xs: 12, sm: 6, md: 4 }} sx={{ display: 'flex' }}>
              <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column', boxShadow: 2, borderRadius: 3 }}>
                <Box
                  component="img"
                  src={blog.image}
                  alt={blog.title}
                  sx={{
                    width: '100%',
                    height: 180,
                    objectFit: 'cover',
                    borderTopLeftRadius: 12,
                    borderTopRightRadius: 12,
                  }}
                />
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    {blog.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#555', mb: 2 }}>
                    {blog.summary}
                  </Typography>
                </CardContent>
                <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
                  <Button
                    variant="text"
                    color={colors.secondary.main}
                    href={`/blog/${blog.slug}`}
                    sx={{
                      borderRadius: 999,
                      px: 2.5,
                      py: 0.7,
                      fontWeight: 600,
                      fontSize: '0.98rem'
                    }}
                  >
                    Read
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}

// ...in your LandingPage component, render BlogSection before Footer:

export default function LandingPage() {
  return (
    <Box sx={{ background: '#fff', minHeight: '100vh', width: '100vw', overflowX: 'hidden' }}>
      <Navbar />
      <Box component='main' sx={{ pt: 9, pb: 8 }}>
        {/* <IntroSection /> */}
        <HeroSection />
        <CustomerIntegrationStrip />
        <PlatformSection />
        <ProductsSection />
        <PricingSection />
        <TestimonialSection />
        <BlogSection /> {/* <-- Add Blog section here */}
      </Box>
      <Footer />
      {/* <CustomerBar /> */}
    </Box>
  );
}